import { useEffect } from 'react'

interface UseKeyPressOptions {
  key: string
  onPress: () => void
  enabled?: boolean
  /**
   * When true, requires Meta (⌘) or Ctrl to be held.
   * Use for cross-platform “command” shortcuts.
   */
  modKey?: boolean
  /**
   * When true, requires Shift. When false, rejects Shift so unmodified
   * shortcuts do not also fire for Shift+Key variants.
   * When omitted, Shift is ignored.
   */
  shiftKey?: boolean
}

export const useKeyPress = ({ key, onPress, enabled = true, modKey = false, shiftKey }: UseKeyPressOptions): void => {
  useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (event: KeyboardEvent): void => {
      // event.key can be undefined for some IME / autofill / extension keydowns
      if (event.key?.toLowerCase() !== key.toLowerCase()) {
        return
      }

      if (modKey && !(event.metaKey || event.ctrlKey)) {
        return
      }

      if (shiftKey === true && !event.shiftKey) {
        return
      }

      if (shiftKey === false && event.shiftKey) {
        return
      }

      event.preventDefault()
      onPress()
    }

    globalThis.addEventListener('keydown', handleKeyDown)

    return () => {
      globalThis.removeEventListener('keydown', handleKeyDown)
    }
  }, [key, onPress, enabled, modKey, shiftKey])
}
